import { useState } from 'react'
import './App.css'
import NewsSection from './NewsSection'
import SEO from './components/SEO'
import ShopSection from './components/ShopSection'

function App() {
  const [activeTab, setActiveTab] = useState('home')
  const [searchQuery, setSearchQuery] = useState('')

  // Navigation items
  const navItems: Array<{id: string; label: string}> = []

  // SSCI Discipline Categories
  const categories = [
    { id: 'economics', name: 'Economics', icon: '📈', count: 42 },
    { id: 'psychology', name: 'Psychology', icon: '🧠', count: 38 },
    { id: 'sociology', name: 'Sociology', icon: '🏛️', count: 35 },
    { id: 'politics', name: 'Political Science', icon: '🗳️', count: 31 },
    { id: 'education', name: 'Education', icon: '🎓', count: 28 },
    { id: 'business', name: 'Business & Management', icon: '💼', count: 45 },
    { id: 'law', name: 'Law', icon: '⚖️', count: 27 },
    { id: 'communication', name: 'Communication', icon: '📡', count: 25 }
  ]

  // Featured SSCI KOLs (Key Opinion Leaders)
  const featuredKOLs = [
    {
      id: 1,
      name: 'Prof. Esther Duflo',
      institution: 'MIT Economics',
      researchArea: 'Development Economics, Poverty Alleviation',
      hIndex: 89,
      url: 'https://economics.mit.edu/faculty/eduflo',
      category: 'economics',
      awards: ['Nobel Prize 2019'],
      tags: ['development', 'experimental', 'policy']
    },
    {
      id: 2,
      name: 'Prof. Daniel Kahneman',
      institution: 'Princeton University',
      researchArea: 'Behavioral Economics, Judgment & Decision Making',
      hIndex: 156,
      url: 'https://psychology.princeton.edu/people/daniel-kahneman',
      category: 'psychology',
      awards: ['Nobel Prize 2002'],
      tags: ['behavioral', 'decision', 'cognitive']
    },
    {
      id: 3,
      name: 'Prof. Amartya Sen',
      institution: 'Harvard University',
      researchArea: 'Welfare Economics, Social Choice Theory',
      hIndex: 134,
      url: 'https://scholar.harvard.edu/sen',
      category: 'economics',
      awards: ['Nobel Prize 1998'],
      tags: ['welfare', 'development', 'ethics']
    },
    {
      id: 4,
      name: 'Prof. Robert Putnam',
      institution: 'Harvard University',
      researchArea: 'Social Capital, Civic Engagement',
      hIndex: 98,
      url: 'https://www.hks.harvard.edu/faculty/robert-putnam',
      category: 'sociology',
      awards: ['National Humanities Medal'],
      tags: ['social capital', 'democracy', 'community']
    },
    {
      id: 5,
      name: 'Prof. Cass Sunstein',
      institution: 'Harvard Law School',
      researchArea: 'Behavioral Law & Economics, Nudge Theory',
      hIndex: 112,
      url: 'https://hls.harvard.edu/faculty/directory/10658/Sunstein',
      category: 'law',
      awards: ['Holberg Prize'],
      tags: ['behavioral', 'regulation', 'nudge']
    },
    {
      id: 6,
      name: 'Prof. Nancy Fraser',
      institution: 'The New School',
      researchArea: 'Critical Theory, Feminist Theory, Social Justice',
      hIndex: 67,
      url: 'https://www.newschool.edu/nssr/faculty/nancy-fraser/',
      category: 'sociology',
      awards: ['Rosa Luxemburg Prize'],
      tags: ['feminist', 'critical theory', 'justice']
    }
  ]

  // Filter KOLs based on search
  const filteredKOLs = featuredKOLs.filter(kol =>
    kol.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kol.institution.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kol.researchArea.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kol.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  return (
    <>
      <SEO 
        title="SSCIKOL - SSCI Field Key Opinion Leader Navigation"
        description="Comprehensive navigation platform for SSCI (Social Sciences Citation Index) key opinion leaders, academic research, and scholarly resources. Discover top scholars and research institutions."
        keywords="SSCI, social sciences, academic research, scholarly journals, key opinion leaders, citation index,社会科学,学术研究,学术期刊,关键意见领袖,引用指数"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo and Title */}
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl font-bold">SS</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">SSCIKOL</h1>
                <p className="text-gray-600 text-sm">SSCI Field KOL Navigation Website</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === item.id
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search scholars, institutions, research fields..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="absolute left-3 top-3 text-gray-400">🔍</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-100">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-blue-500">📚</span>
              <span className="text-sm font-medium">Social Sciences Citation Index (SSCI)</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Global SSCI Field
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600"> Key Opinion Leader</span>
              Navigation
            </h1>
            
            <p className="text-xl text-gray-700 mb-10 max-w-3xl">
              We provide you with navigation to key opinion leader (KOL) information in the global SSCI (Social Sciences Citation Index) field.
              Explore top scholars, research institutions, core journals, and cutting-edge research areas.
            </p>

            <div className="hidden"></div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <ShopSection />

      {/* Featured KOLs Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Top SSCI Scholars</h2>
              <p className="text-gray-600">Leading figures and Nobel laureates in various social science fields</p>
            </div>
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredKOLs.map((kol) => (
              <div key={kol.id} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mb-2">
                        {kol.category}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{kol.name}</h3>
                      <p className="text-gray-600 text-sm mb-1">{kol.institution}</p>
                    </div>
                    <div className="flex items-center bg-gray-100 rounded-lg px-2 py-1">
                      <span className="text-blue-500 mr-1">h-index</span>
                      <span className="font-medium">{kol.hIndex}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{kol.researchArea}</p>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Major Awards:</h4>
                    <div className="flex flex-wrap gap-2">
                      {kol.awards.map((award, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                          {award}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {kol.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <a 
                    href={kol.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors duration-200"
                  >
                    Visit Scholar Profile
                    <span className="ml-2">→</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic News Section */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <NewsSection website="sscikol" limit={10000} title="SSCI Research News" themeColor="blue" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-lg font-bold">SS</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">SSCIKOL</h3>
                  <p className="text-gray-400 text-sm">SSCI Field KOL Navigation Website</p>
                </div>
              </div>
              <p className="text-gray-400">
                We are committed to providing key opinion leader (KOL) information in the global SSCI (Social Sciences Citation Index) field.
                This website collects and organizes information on top scholars, research institutions, core journals, and cutting-edge research areas.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Top Scholars
                  </a>
                </li>
                <li>
                  <a 
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    SSCI Journals
                  </a>
                </li>
                <li>
                  <a 
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Research Institutions
                  </a>
                </li>
                <li>
                  <a 
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Research Areas
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Contact & Collaboration</h4>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-center space-x-3">
                  <span>📧</span>
                  <a 
                    href="mailto:info@sscikol.com"
                    className="hover:text-white transition-colors duration-200"
                  >
                    info@sscikol.com
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <span>🌐</span>
                  <a 
                    href="https://sscikol.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors duration-200"
                  >
                    sscikol.com
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <span>🔗</span>
                  <a 
                    href="https://scikol.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors duration-200"
                  >
                    scikol.com
                  </a>
                </div>
                <p className="text-sm pt-4">
                  This website is dedicated to promoting exchange and collaboration in social science research.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© 2026 SSCIKOL. All rights reserved. | SSCI Field Key Opinion Leader Navigation Website</p>
            <p className="text-sm mt-2">This website collects and organizes information on scholars, institutions, and research achievements in the global SSCI field.</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}

export default App